/**
 * DrilldownRow — a single data-point comparison row.
 * Used inside ScoreCardBody for both desktop and mobile.
 *
 * Spec §E.6, §F.1: tooltip uses role="tooltip" + aria-describedby.
 * "Learn more →" links to /glossary#{id} when a data-point ID is provided.
 */

interface DeltaDescriptor {
  direction: 'improved' | 'worsened' | 'unchanged' | 'incomparable';
  display: string;
  magnitude?: number;
}

export interface DrilldownRowProps {
  id: string;
  label: string;
  description: string;
  thenDisplay: string;
  nowDisplay: string;
  delta: DeltaDescriptor;
  /** Optional external URL — renders an external-link icon next to nowDisplay. */
  externalUrl?: string;
}

const DELTA_COLORS: Record<string, string> = {
  improved: 'text-emerald-600',
  worsened: 'text-rose-600',
  unchanged: 'text-slate-400',
  incomparable: 'text-slate-400',
};

export function DrilldownRow({ id, label, description, thenDisplay, nowDisplay, delta, externalUrl }: DrilldownRowProps) {
  const tooltipId = `dp-tip-${id}`;
  return (
    <div className="flex items-start justify-between py-2.5 border-b border-slate-100 last:border-0 gap-4">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-sm text-slate-700 font-medium truncate">{label}</span>
          {description && (
            <span className="relative inline-flex group/dptip flex-shrink-0">
              <button
                type="button"
                aria-describedby={tooltipId}
                className="text-slate-300 hover:text-slate-500 focus-visible:text-slate-500 cursor-help p-0 bg-transparent border-0 leading-none transition-colors"
              >
                <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                  <circle cx="8" cy="8" r="7.5" stroke="currentColor" strokeWidth="1" fill="none" />
                  <text x="8" y="12" textAnchor="middle" fontSize="10" fontWeight="bold">i</text>
                </svg>
                <span className="sr-only">More information about {label}</span>
              </button>
              <div
                id={tooltipId}
                role="tooltip"
                className="hidden group-hover/dptip:block group-focus-within/dptip:block absolute bottom-full left-0 mb-2 w-64 bg-slate-800 text-white text-xs rounded-lg p-3 shadow-xl z-50 pointer-events-auto"
              >
                <p className="leading-relaxed">{description}</p>
                <a
                  href={`/glossary#${id}`}
                  className="mt-2 block text-emerald-400 hover:text-emerald-300 font-medium"
                >
                  Learn more →
                </a>
                <div
                  aria-hidden="true"
                  className="absolute top-full left-4 w-0 h-0 border-x-[5px] border-x-transparent border-t-[5px] border-t-slate-800"
                />
              </div>
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-6 flex-shrink-0" style={{ fontVariantNumeric: 'tabular-nums' }}>
        <span className="text-sm text-slate-400 font-mono w-12 text-right">{thenDisplay}</span>
        <span className="text-sm text-slate-700 font-mono w-12 text-right inline-flex items-center justify-end gap-1">
          {nowDisplay}
          {externalUrl && (
            <a
              href={externalUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Opens in new tab"
              className="text-slate-400 hover:text-emerald-600 transition-colors flex-shrink-0"
              onClick={(e) => e.stopPropagation()}
            >
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          )}
        </span>
        <span className={`text-xs font-medium w-16 text-right ${DELTA_COLORS[delta.direction]}`}>
          {delta.display}
        </span>
      </div>
    </div>
  );
}
